import React, { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  Target, Plus, CheckCircle2, Trash2, BookOpen,
  Award, Flame, Dumbbell, Gamepad2, Heart
} from "lucide-react";
import { toast } from "sonner";
import { JournalEntry } from "./DashboardWidgets";

export const GoalsCard = ({ goals: initialGoals, t }) => {
  const [goals, setGoals] = useState(initialGoals);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [newGoalTitle, setNewGoalTitle] = useState("");
  const [newGoalTarget, setNewGoalTarget] = useState(10);

  const addGoal = async () => {
    if (!newGoalTitle.trim()) return;
    try {
      const res = await axios.post(`${API}/goals`, {
        title: newGoalTitle.trim(), target: newGoalTarget, category: "general", unit: "times"
      });
      setGoals(prev => [res.data.goal, ...prev]);
      setNewGoalTitle("");
      setNewGoalTarget(10);
      setShowGoalForm(false);
      toast.success(t("dashboard.goalCreated"));
    } catch {
      toast.error(t("dashboard.goalCreateFailed"));
    }
  };

  const incrementGoal = async (goalId) => {
    try {
      const res = await axios.put(`${API}/goals/${goalId}/progress`, { increment: 1 });
      setGoals(prev => prev.map(g => g.id === goalId ? res.data.goal : g));
      if (res.data.goal.completed) toast.success(t("dashboard.goalCompleted"));
    } catch {
      toast.error(t("dashboard.goalUpdateFailed"));
    }
  };

  const deleteGoal = async (goalId) => {
    try {
      await axios.delete(`${API}/goals/${goalId}`);
      setGoals(prev => prev.filter(g => g.id !== goalId));
    } catch {
      toast.error(t("dashboard.goalDeleteFailed"));
    }
  };

  return (
    <div className="glass-card p-6" data-testid="goals-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
          <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>Goals</h3>
        </div>
        <Button size="icon" variant="ghost" onClick={() => setShowGoalForm(!showGoalForm)} className="text-sky-500 h-8 w-8" data-testid="add-goal-btn">
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {showGoalForm && (
        <div className="bg-white/50 rounded-xl p-3 mb-3 space-y-2" data-testid="goal-form">
          <Input
            value={newGoalTitle}
            onChange={(e) => setNewGoalTitle(e.target.value)}
            placeholder="e.g. Meditate 20 times this month"
            className="text-sm"
            data-testid="goal-title-input"
            onKeyDown={(e) => e.key === "Enter" && addGoal()}
          />
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={newGoalTarget}
              onChange={(e) => setNewGoalTarget(parseInt(e.target.value) || 1)}
              className="w-20 text-sm"
              min={1}
              data-testid="goal-target-input"
            />
            <span className="text-xs text-slate-500">target</span>
            <Button size="sm" onClick={addGoal} className="glow-button ml-auto" data-testid="save-goal-btn">Create</Button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-48 overflow-y-auto">
        {goals.length === 0 && !showGoalForm && (
          <p className="text-sm text-slate-500 text-center py-3">Set goals to track your progress</p>
        )}
        {goals.slice(0, 5).map(goal => (
          <div key={goal.id} className="flex items-center gap-3 bg-white/40 rounded-xl p-3 group" data-testid={`goal-${goal.id}`}>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${goal.completed ? "text-green-600 line-through" : "text-sky-800"}`}>{goal.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <Progress value={(goal.current / goal.target) * 100} className="h-1.5 flex-1 bg-sky-100" />
                <span className="text-xs text-slate-500 whitespace-nowrap">{goal.current}/{goal.target}</span>
              </div>
            </div>
            {!goal.completed && (
              <Button size="icon" variant="ghost" onClick={() => incrementGoal(goal.id)} className="h-7 w-7 text-sky-500 hover:text-sky-700" data-testid={`increment-goal-${goal.id}`}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            )}
            {goal.completed && <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />}
            <button onClick={() => deleteGoal(goal.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-400 transition-all">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const JournalCard = ({ journal: initialJournal, t }) => {
  const [journal, setJournal] = useState(initialJournal);
  const [journalText, setJournalText] = useState("");
  const [journalMood, setJournalMood] = useState("neutral");
  const [showJournalForm, setShowJournalForm] = useState(false);

  const addJournalEntry = async () => {
    if (!journalText.trim()) return;
    try {
      const res = await axios.post(`${API}/journal`, { text: journalText.trim(), mood_tag: journalMood });
      setJournal(prev => [res.data.entry, ...prev]);
      setJournalText("");
      setShowJournalForm(false);
      toast.success(t("dashboard.journalSaved"));
    } catch {
      toast.error(t("dashboard.journalSaveFailed"));
    }
  };

  const deleteJournalEntry = async (id) => {
    try {
      await axios.delete(`${API}/journal/${id}`);
      setJournal(prev => prev.filter(e => e.id !== id));
    } catch {
      toast.error(t("dashboard.journalDeleteFailed"));
    }
  };

  return (
    <div className="glass-card p-6" data-testid="journal-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
          <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>Journal</h3>
        </div>
        <Button size="icon" variant="ghost" onClick={() => setShowJournalForm(!showJournalForm)} className="text-sky-500 h-8 w-8" data-testid="add-journal-btn">
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {showJournalForm && (
        <div className="bg-white/50 rounded-xl p-3 mb-3 space-y-2" data-testid="journal-form">
          <Input
            value={journalText}
            onChange={(e) => setJournalText(e.target.value)}
            placeholder={t("dashboard.journalPlaceholder")}
            className="text-sm"
            data-testid="journal-text-input"
            onKeyDown={(e) => e.key === "Enter" && addJournalEntry()}
          />
          <div className="flex items-center gap-1.5">
            {["great", "good", "neutral", "tired", "stressed"].map(mood => (
              <button
                key={mood}
                onClick={() => setJournalMood(mood)}
                className={`text-xs px-2 py-1 rounded-full transition-all ${
                  journalMood === mood
                    ? "bg-sky-500 text-white"
                    : "bg-white/50 text-slate-500 hover:bg-white"
                }`}
                data-testid={`mood-${mood}`}
              >
                {mood}
              </button>
            ))}
            <Button size="sm" onClick={addJournalEntry} className="glow-button ml-auto" data-testid="save-journal-btn">Save</Button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-48 overflow-y-auto">
        {journal.length === 0 && !showJournalForm && (
          <p className="text-sm text-slate-500 text-center py-3">Record your thoughts after each session</p>
        )}
        {journal.slice(0, 5).map(entry => (
          <JournalEntry key={entry.id} entry={entry} onDelete={deleteJournalEntry} />
        ))}
      </div>
    </div>
  );
};

export const PersonalBestsCard = ({ personalBests }) => (
  <div className="glass-card p-6" data-testid="personal-bests-card">
    <div className="flex items-center gap-2 mb-4">
      <Award className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
      <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>Personal Bests</h3>
    </div>
    {personalBests ? (
      <div className="grid grid-cols-2 gap-3">
        {[
          { icon: Flame, value: personalBests.longest_streak, label: "Best Streak", color: "text-orange-500" },
          { icon: Dumbbell, value: personalBests.total_exercises, label: "Exercises Done", color: "text-cyan-500" },
          { icon: Gamepad2, value: personalBests.total_games_played, label: "Games Played", color: "text-indigo-500" },
          { icon: Heart, value: personalBests.total_meditation_minutes, label: "Med. Minutes", color: "text-pink-500" },
        ].map(({ icon: Icon, value, label, color }) => (
          <div key={label} className="bg-white/40 rounded-xl p-3 text-center">
            <Icon className={`w-5 h-5 ${color} mx-auto mb-1`} strokeWidth={1.5} />
            <p className="text-lg font-semibold text-sky-900" style={{ fontFamily: 'Outfit' }}>{value}</p>
            <p className="text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-slate-500">Start training to see your records!</p>
    )}
  </div>
);
